import { Bed, Eye, Heart, MapPin, Maximize } from "lucide-react";
import { backendFeatures } from "../../config/backendFeatures";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { sourceFromPath } from "../../shared/tracking/analyticsTracker";
import { useImpressionRef } from "../../shared/tracking/useImpressionRef";
import { Swiper, SwiperSlide } from "swiper/react";
import { useToggleMarkFavoriteProperty } from "../../hooks/useFavorites";
import { useIsBlocked } from "../../store/useBlockStore";
import { useIsViewed } from "../../store/useViewedStore";
import { formatTimeAgo } from "../../helpers/date";
import { listingPath } from "../../helpers/listingPath";
import { hasBurnedWatermark, photoSrcSet, thumbUrl } from "../../helpers/imageUrl";
import { Listing } from "../../types/listings/listing.model";

import { ListingBadgePill } from "./ListingPagePill";
import { OwnerBadge } from "./OwnerBadge";
import { areaText, displayTitle, hasRooms, regionLabel } from "../../helpers/listingText";
import {
  LISTING_BADGE_THEMES,
  listingBadgeKey,
} from "../../helpers/constants/listingBadges";

interface ListingCardProps {
  listing: Listing;
  compact?: boolean;
  isLoading?: boolean;
  /** "card" — vertical photo-on-top card (default).
   *  "row" — horizontal list row: medium thumbnail left, price/title/
   *          location/stats stacked right.
   *  "compact" — dense list line: small thumbnail + price/title + one
   *          meta line, heart on the right. */
  variant?: "card" | "row" | "compact";
  setIsFavoriteChanged: (
    isFavoriteChanged: boolean | ((prev: boolean) => boolean),
  ) => void;
  type?: string;
}

export function ListingCard({
  listing,
  compact = false,
  isLoading = false,
  variant = "card",
  setIsFavoriteChanged,
  type,
}: ListingCardProps) {
  const { t, i18n } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const toggleMarkFavoriteProperty =
    useToggleMarkFavoriteProperty(setIsFavoriteChanged);

  // Every list in the app renders through this card, so hiding a blocked
  // seller here covers search, the home feed, favourites, similar listings and
  // the owner profiles at once. Lists hand this card the raw API item
  // (`user`), the detail mapper produces `seller`, and favourites nest
  // either one under `property` — hence the four lookups.
  const raw = listing as any;
  // Favourites: the wrapper's `user` is whoever saved it (the viewer), so
  // the nested listing's seller must win.
  const sellerId = raw?.property
    ? raw.property.seller?.id ?? raw.property.user?.id
    : raw?.seller?.id ?? raw?.user?.id;
  const sellerIsBlocked = useIsBlocked(sellerId);

  const listingId = Number(listing.property_id || listing.id);
  const alreadyViewed = useIsViewed(listingId);

  // Аналитика: показ карточки (≥50% ≥1 c) и источник для страницы объявления.
  // A/B-тест фото: backend может прислать обложку варианта и метку теста.
  const { pathname } = useLocation();
  const tracked = (listing?.property ?? listing) as any;
  const promotionType: "top" | "vip" | "urgent" | undefined = tracked?.is_urgent
    ? "urgent"
    : tracked?.is_vip
      ? "vip"
      : tracked?.is_top
        ? "top"
        : undefined;
  const abAssignment = tracked?.abTest as { testId: string; variantId: "A" | "B" } | null | undefined;
  const pageSource = sourceFromPath(pathname);
  const impressionRef = useImpressionRef(
    listingId > 0
      ? {
          listingId,
          source: pageSource,
          promotionType,
          abTestId: abAssignment?.testId,
          abVariantId: abAssignment?.variantId,
        }
      : null,
  );
  const linkState = { src: promotionType ?? pageSource };

  if (sellerIsBlocked) return null;

  const property = listing?.property ? listing.property : listing;
  const badgeType = listingBadgeKey(property);

  const badge = badgeType ? LISTING_BADGE_THEMES[badgeType] : null;
  const toggleFavorite = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    toggleMarkFavoriteProperty.mutate(listingId);
  };

  // ─── Skeleton ───────────────────────────────────────────────────────────────
  if (isLoading) {
    if (variant === "compact") {
      return (
        <div className="animate-pulse flex items-center gap-3 rounded-xl bg-white p-2">
          <div className="bg-gray-200 rounded-lg w-[76px] h-[76px] shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="bg-gray-200 h-3.5 rounded-full w-1/4" />
            <div className="bg-gray-100 h-3 rounded-full w-3/4" />
            <div className="bg-gray-100 h-2.5 rounded-full w-1/2" />
          </div>
        </div>
      );
    }
    if (variant === "row") {
      return (
        <div className="animate-pulse flex gap-3 sm:gap-4 rounded-2xl bg-white p-2.5 sm:p-3">
          <div className="bg-gray-200 rounded-xl w-28 h-28 sm:w-40 sm:h-32 shrink-0" />
          <div className="flex-1 py-1 space-y-2">
            <div className="bg-gray-200 h-4 rounded-full w-1/4" />
            <div className="bg-gray-100 h-3 rounded-full w-3/4" />
            <div className="bg-gray-100 h-3 rounded-full w-1/2" />
          </div>
        </div>
      );
    }
    return (
      <div
        className={`animate-pulse rounded-2xl overflow-hidden bg-white ${
          compact ? "h-40" : "h-72"
        }`}
      >
        <div className="bg-gray-200 h-2/3 w-full" />
        <div className="p-4 h-1/3 space-y-2">
          <div className="bg-gray-200 h-4 rounded-full w-1/3" />
          <div className="bg-gray-100 h-3 rounded-full w-2/3" />
          <div className="bg-gray-100 h-3 rounded-full w-1/2" />
        </div>
      </div>
    );
  }

  const isFavorite = property.isLike || property.is_like || type === "favorite";
  // `property`, not `listing`: favourites wrap the listing, so the wrapper
  // has no price and every saved listing read «Келишимдик баа».
  const isNegotiable =
    Boolean(property.is_negotiable) || property.price === "0" || !Number(property.price);
  // The seller's own currency — KGS listings used to show a "$" price.
  const isKgs = property.currency === "KGS";
  const som = t("createListing.currency.somLabel", { defaultValue: "сом" });
  const fmtMoney = (n: number) => `${Math.round(n).toLocaleString("ru-RU")} ${isKgs ? som : "$"}`;
  const priceLabel = isNegotiable ? t("createListing.negotiable.label") : fmtMoney(Number(property.price));
  // Price per m² helps compare flats of different size (not for plots in sotka).
  const areaNum = Number(String(property.area ?? "").replace(",", ".")) || 0;
  const perM2 =
    !isNegotiable && areaNum > 0 && String(property.type || "").toUpperCase() !== "PLOT"
      ? `${fmtMoney(Number(property.price) / areaNum)}/м²`
      : "";
  // Region in the UI language for every region (TALAS etc. included).
  const locationLabel = `${regionLabel(property.region, i18n.language)}${
    property.district && property.district !== "bishkek"
      ? ` · ${t(`districts.${property.district}`, { defaultValue: property.district })}`
      : ""
  }`;
  // Commerce and plots have no rooms — don't print "2 бөлмө" on a shop.
  const roomsValue = hasRooms(property.type || property.category)
    ? property.rooms_count || property.room_count
    : undefined;
  const title = displayTitle(property, t, i18n.language);

  // ── Card face (Cian/Samolet-style: image-forward, one-line specs) ──────────
  const catRaw = String(property.type || property.category || "").toLowerCase();
  const CATEGORY_LABELS: Record<string, string> = {
    apartment: t("property.apartment", { defaultValue: "Квартира" }),
    house: t("property.house", { defaultValue: "Дом" }),
    commercial: t("property.commercial", { defaultValue: "Коммерческое" }),
    land: t("property.land", { defaultValue: "Участок" }),
    plot: t("property.land", { defaultValue: "Участок" }),
    room: t("property.room", { defaultValue: "Комната" }),
  };
  const specParts = [
    roomsValue
      ? `${roomsValue} ${t("listing.roomsShort", { defaultValue: "ком" })}`
      : CATEGORY_LABELS[catRaw] || "",
    areaText(property.area, property.type || property.category, t),
    property.floor && property.floors_total
      ? `${property.floor}/${property.floors_total} ${t("listing.floorShort", { defaultValue: "этаж" })}`
      : "",
  ].filter(Boolean);
  const specsLine = specParts.join(" · ");

  const cardTimeAgo = formatTimeAgo(
    property.createdAt ||
      property.created_at ||
      listing.createdAt ||
      listing.created_at,
    t,
  );

  const coverPhotoUrl: string | undefined = (property as any).coverPhotoUrl;
  const cardImages: string[] = coverPhotoUrl
    ? [coverPhotoUrl, ...(property.images ?? []).filter((u: string) => u !== coverPhotoUrl)]
    : property.images ?? [];
  const dotCount = Math.min(cardImages.length, compact ? 5 : 8);
  const dotActive =
    cardImages.length > dotCount
      ? Math.round((activeIndex / (cardImages.length - 1)) * (dotCount - 1))
      : activeIndex;

  // ─── Compact variant — dense list line ──────────────────────────────────────
  if (variant === "compact") {
    const firstImage = cardImages[0];
    return (
      <Link
        ref={impressionRef}
        state={linkState}
        to={listingPath({ id: listingId, title: property.title })}
        className="group flex items-start gap-3 bg-white rounded-xl transition-all duration-200 p-2"
      >
        {/* Thumbnail */}
        <div className="relative w-[76px] h-[76px] rounded-lg overflow-hidden shrink-0 bg-[#0f172a]/5">
          {firstImage ? (
            <img
              src={thumbUrl(firstImage, 76)}
              alt={title}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300">
              <MapPin className="w-5 h-5" />
            </div>
          )}
          {property.images?.length > 1 && (
            <div className="absolute bottom-1 right-1 bg-black/40 backdrop-blur-sm text-white text-[9px] px-1 py-0.5 rounded-full leading-none">
              {property.images.length}
            </div>
          )}
          {alreadyViewed && (
            <span className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[9px] font-medium text-center py-0.5 backdrop-blur-sm">
              {t("listing.viewedByYou", { defaultValue: "Вы смотрели" })}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 self-center">
          <div className="flex min-w-0 items-center gap-1.5">
            <p
              className="shrink-0 font-bold tracking-tight text-sm text-blue-600"
            >
              {priceLabel}
            </p>
            <OwnerBadge listing={listing} size="sm" />
          </div>
          <h2
            className={`text-[13px] font-semibold text-gray-900 leading-snug truncate group-hover:text-blue-300 transition-colors`}
          >
            {title}
          </h2>
          <p className="text-[11px] text-gray-400 truncate mt-0.5">
            {areaText(property.area, property.type || property.category, t)}
            {roomsValue
              ? ` · ${roomsValue} ${t("listing.roomsShort", { defaultValue: "ком" })}`
              : ""}
            {" · "}
            {locationLabel}
          </p>
        </div>

        {/* Favorite */}
        <button
          onClick={toggleFavorite}
          aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
          className="shrink-0 self-center p-1.5 rounded-full hover:bg-gray-50 active:scale-95 transition"
        >
          <Heart
            className={`w-4 h-4 transition-colors ${
              isFavorite ? "text-red-500 fill-red-500" : "text-gray-400"
            }`}
          />
        </button>
      </Link>
    );
  }

  // ─── Row variant — horizontal list item ──────────────────────────────────────
  if (variant === "row") {
    const firstImage = cardImages[0];
    return (
      <Link
        ref={impressionRef}
        state={linkState}
        to={listingPath({ id: listingId, title: property.title })}
        className="group relative flex gap-3 sm:gap-4 bg-white rounded-3xl transition-all duration-200 p-3"
      >
        {/* Floating favorite — sits in the top-right corner of the card */}
        <button
          onClick={toggleFavorite}
          aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
          className="absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center rounded-full bg-white shadow-[0_3px_12px_rgba(0,0,0,0.14)] hover:shadow-[0_3px_16px_rgba(0,0,0,0.2)] active:scale-90 transition-all"
        >
          <Heart
            className={`h-5 w-5 transition-colors ${
              isFavorite ? "fill-blue-600 text-blue-600" : "text-gray-300"
            }`}
            strokeWidth={2.25}
          />
        </button>

        {/* Thumbnail */}
        <div className="relative w-28 h-28 sm:w-40 sm:h-40 rounded-2xl overflow-hidden shrink-0 bg-[#0f172a]/5">
          {firstImage ? (
            <img
              src={thumbUrl(firstImage, 160)}
              alt={title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-slate-300">
              <MapPin className="w-7 h-7" />
              <span className="text-[10px] text-slate-400 font-medium">
                {t("listing.noPhoto", { defaultValue: "Нет фото" })}
              </span>
            </div>
          )}

          {/* B8: photos carry a real watermark once the server adds it. */}
          {!backendFeatures.watermark && !hasBurnedWatermark(firstImage) && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
              <span
                style={{
                  color: "white",
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 700,
                  fontSize: "13px",
                  letterSpacing: "1px",
                  opacity: 0.28,
                  textShadow: "0 2px 4px rgba(0,0,0,0.6)",
                }}
              >
                Turaq.kg
              </span>
            </div>
          )}

          {property.images?.length > 1 && (
            <div className="absolute bottom-1.5 right-1.5 bg-black/45 backdrop-blur-sm text-white text-[10px] px-1.5 py-0.5 rounded-full">
              {property.images.length}
            </div>
          )}

          {alreadyViewed && (
            <span className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-black/55 backdrop-blur-sm px-2 py-1 text-[10px] font-semibold text-white">
              <Eye className="h-3 w-3" />
              {t("listing.viewedByYou", { defaultValue: "Вы смотрели" })}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col py-0.5 pr-10">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <p
              className="shrink-0 font-extrabold tracking-tight text-lg sm:text-xl text-blue-600"
            >
              {priceLabel}
            </p>
            <OwnerBadge listing={listing} />
          </div>
          {perM2 && <p className="mt-0.5 text-xs font-medium text-gray-400">{perM2}</p>}

          <h2 className="mt-1 font-bold text-gray-900 text-sm sm:text-base leading-snug line-clamp-2 group-hover:text-blue-300 transition-colors">
            {title}
          </h2>

          <div className="flex items-center gap-1.5 text-gray-400 mt-1.5">
            <MapPin className="w-4 h-4 shrink-0 text-blue-500" />
            <span className="text-[13px] text-gray-500 truncate">
              {locationLabel}
            </span>
          </div>

          <div className="mt-auto pt-3 flex items-center text-gray-500 text-[11px] sm:text-xs whitespace-nowrap overflow-hidden">
            <span className="flex items-center gap-1.5">
              <Maximize className="w-4 h-4 text-gray-400 shrink-0" />
              {areaText(property.area, property.type || property.category, t)}
            </span>
            {roomsValue && (
              <>
                <span className="mx-2.5 h-4 w-px bg-gray-200 shrink-0" />
                <span className="flex items-center gap-1.5">
                  <Bed className="w-4 h-4 text-gray-400 shrink-0" />
                  {roomsValue}{" "}
                  {t("listing.roomsShort", { defaultValue: "ком" })}
                </span>
              </>
            )}
            <span className="mx-2.5 h-4 w-px bg-gray-200 shrink-0" />
            <span className="flex items-center gap-1.5">
              <Eye className="w-4 h-4 text-gray-400 shrink-0" />
              {property.views ?? 0}
            </span>
          </div>
        </div>
      </Link>
    );
  }
  return (
    <Link
      ref={impressionRef}
      state={linkState}
      to={listingPath({ id: listingId, title: property.title })}
      className="group block overflow-hidden rounded-2xl bg-white transition-shadow duration-200"
    >
      {/* ── IMAGE ───────────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-slate-100 aspect-[3/2]">
        <Swiper
          onSlideChange={(s) => setActiveIndex(s.activeIndex)}
          preventClicks={false}
          preventClicksPropagation={false}
          className="h-full w-full"
        >
          {cardImages.length > 0 ? (
            cardImages.map((img: string, index: number) => (
              <SwiperSlide key={index}>
                {/* Only the visible photo and its neighbours get a src: otherwise
                    every card downloads its whole gallery up front. */}
                {Math.abs(index - activeIndex) <= 1 ? (
                  <img
                    src={thumbUrl(img, 400)}
                    // Two cards a row on phones (~180 px): the browser picks the smaller file.
                    srcSet={photoSrcSet(img, [200, 400])}
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 300px"
                    alt={title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    loading="lazy"
                    decoding="async"
                  />
                ) : null}
              </SwiperSlide>
            ))
          ) : (
            <SwiperSlide>
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-slate-100 text-slate-300">
                <MapPin className="h-8 w-8" />
                <span className="text-xs font-medium text-slate-400">
                  {t("listing.noPhoto", { defaultValue: "Нет фото" })}
                </span>
              </div>
            </SwiperSlide>
          )}
        </Swiper>

        {/* Badge — TOP / VIP / URGENT */}
        {badge && <ListingBadgePill badge={badge} />}

        {/* Favorite */}
        <button
          onClick={toggleFavorite}
          aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
          className="absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full bg-white shadow-[0_3px_12px_rgba(0,0,0,0.16)] transition-all duration-200 hover:scale-105 active:scale-90"
        >
          <Heart
            className={`h-[18px] w-[18px] transition-colors ${
              isFavorite ? "fill-blue-600 text-blue-600" : "text-gray-300"
            }`}
            strokeWidth={2.25}
          />
        </button>

        {/* Photo progress dots */}
        {dotCount > 1 && (
          <div className="absolute inset-x-0 bottom-2.5 z-10 flex justify-center gap-1">
            {Array.from({ length: dotCount }).map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full bg-white shadow-[0_0_2px_rgba(0,0,0,0.4)] transition-all duration-300 ${
                  i === dotActive ? "w-3.5" : "w-1 bg-white/60"
                }`}
              />
            ))}
          </div>
        )}

        {/* "Вы смотрели" — this device already opened this listing */}
        {alreadyViewed && (
          <span className="absolute bottom-2.5 left-2.5 z-10 flex items-center gap-1 rounded-full bg-black/55 backdrop-blur-sm px-2 py-1 text-[11px] font-semibold text-white">
            <Eye className="h-3 w-3" />
            {t("listing.viewedByYou", { defaultValue: "Вы смотрели" })}
          </span>
        )}
      </div>

      {/* ── TEXT ────────────────────────────────────────────── */}
      <div className={`px-3 ${compact ? "pt-2 pb-2.5" : "pt-3 pb-3.5"}`}>
        {/* Phones (compact): badge always under the price — it used to jump
            between the price line and the next one, so neighbouring cards in
            the 2-column grid didn't line up. */}
        <div
          className={`flex min-w-0 gap-x-2 gap-y-1 ${
            compact
              ? "flex-col items-start sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
              : "flex-wrap items-center justify-between"
          }`}
        >
          <p
            className={`shrink-0 font-bold leading-tight tracking-tight text-gray-900 group-hover:text-blue-300 transition-colors ${
              compact ? "text-base" : "text-[19px]"
            }`}
          >
            {priceLabel}
          </p>
          <OwnerBadge listing={listing} size={compact ? "sm" : "md"} />
        </div>

        <h2
          className={`mt-1 font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-blue-300 transition-colors ${
            compact ? "min-h-[2.75em] text-[13px]" : "text-sm"
          }`}
        >
          {title}
        </h2>

        {specsLine && (
          <p className="mt-1.5 truncate text-[13px] text-gray-500">
            {specsLine}
            {perM2 && <span className="text-gray-400"> · {perM2}</span>}
          </p>
        )}

        <p className="mt-1 flex items-center gap-1 text-[13px] text-gray-500">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <span className="truncate">{locationLabel}</span>
        </p>

        <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-gray-400">
          <span className="flex items-center gap-1">
            <Eye className="h-3.5 w-3.5" />
            {property.views ?? 0}
          </span>
          {cardTimeAgo && (
            <>
              <span className="text-gray-300">·</span>
              <span>{cardTimeAgo}</span>
            </>
          )}
        </p>
      </div>
    </Link>
  );
}
